/**
 * `verifyAttestationBatch` — `@shn/sdk/audit` batch-verification helper for
 * consumer-platform audit-chain rendering surfaces (e.g., Account v0 ops
 * log per `account_v0_home_design.md` §2.5 audit chain View 1
 * patient-perspective).
 *
 * Per VP Engineering directive 2026-05-10 — DEV5 Account v0 ops log Item 3
 * gate (per [DEV5 rotation-8 handoff §"chained-tail item 3"](../../../coordination/handoffs/2026-05-08-from-dev5-to-dev5-rotation-onboarding-8.md)):
 * Account v0 ops log + audit chain View 1 + View 2 substantive composition
 * gates on DEV3 wire-contract `getPatientOpsLog` + DEV6 `@shn/sdk/audit`
 * primitives. This batch-verification helper composes the per-attestation
 * `verifyInclusionProofAttestation` primitive into Account-v0-ops-log-
 * friendly batch shape with categorized verified/failed/no-attestation result.
 *
 * # Phase 4 §4.2 anchored-only emission discrimination handler (3-arm shape)
 *
 * Per [DEV6 Phase 4 SDK consumer surface scaffold design memo §4.2 Path (b)
 * 3-arm explicit `noAttestation` discrimination][1] + [DEV5 rotation-14
 * consumer-contract ping (a)-(d) substantive disposition cycle close][2]:
 * substrate-side writer-config `disableLedger: true` per-entry semantics
 * surface to consumer-side as input `attestation: InclusionProofAttestation
 * | undefined`; helper discriminates `undefined` to `noAttestation` arm
 * preserving operator-visible substrate-state per
 * `feedback_phg_recovery_ux_honesty.md` content-honesty discipline analog.
 *
 * # Composition with consumer-platform ops log rendering
 *
 * ```typescript
 * // Account v0 ops log consumer flow:
 * const entries = await getPatientOpsLog(...);  // DEV3 wire-contract
 * const result = await verifyAttestationBatch(
 *   entries.map(e => ({ id: e.recordId, attestation: e.attestation })),
 *   trustedAttestationKeys
 * );
 * // result.verified:      render with provenance (verifiedKid + attestedAt)
 * // result.failed:        render with error indicator + reason (operator triage)
 * // result.noAttestation: render with "no anchor yet" or "awaiting audit anchor" framing
 * //                       per content-honesty discipline (DEV5 §2(a) refinement canonical)
 * ```
 *
 * # Why batch-shape + 3-arm discrimination
 *
 * - **3-arm categorized result** — verified vs failed vs noAttestation split
 *   at boundary; consumer renders each category with appropriate UI without
 *   per-entry runtime branching at call site (decouples consumer from
 *   writer-config visibility per DEV5 §2(c) substantive disposition)
 * - **ID preservation** — caller-supplied `id` propagates through to all
 *   3 arms; consumer correlates back to source ops log entries
 * - **Sequential await** — preserves Web Crypto API per-call isolation;
 *   parallelism via `Promise.all` if consumer wants it (caller decides)
 * - **Throws only on caller misconfiguration** — empty trustedAttestationKeys
 *   map; Web Crypto API unavailable. Per-attestation verification failures
 *   surface as `result.failed` entries; absent-attestation surfaces as
 *   `result.noAttestation` entries (neither thrown)
 *
 * [1]: ../../../../coordination/2026-05-10-from-dev6-phase-4-sdk-consumer-surface-scaffold-design-memo.md
 * [2]: ../../../../coordination/2026-05-11-from-dev5-to-dev6-phase-4-section-4-2-consumer-contract-disposition-cycle-close.md
 */
import { verifyInclusionProofAttestation } from './attestation-verifier.js';
/**
 * Verify a batch of `InclusionProofAttestation` entries; returns 3-arm
 * categorized verified/failed/no-attestation result.
 *
 * Per Phase 4 §4.2 anchored-only emission discrimination canonical:
 * entries with `attestation === undefined` route to `noAttestation` arm
 * (writer-side `disableLedger: true` per-entry semantics preserved at
 * consumer surface); entries with attestation present route through
 * `verifyInclusionProofAttestation` primitive to verified/failed arms.
 *
 * Sequential per-entry verification (Web Crypto API per-call isolation
 * preserved). Caller may parallelize via `Promise.all` if appropriate;
 * helper preserves substrate-side per-attestation verification primitive
 * shape unchanged.
 *
 * Throws only for caller misconfiguration propagated from underlying
 * primitive:
 * - Empty `trustedAttestationKeys` map (caller must supply at least one
 *   trusted attestation key)
 * - Web Crypto API unavailable (`globalThis.crypto?.subtle` is undefined)
 *
 * Per-attestation verification failures surface as `result.failed` entries
 * with discriminator + detail; absent-attestation entries surface as
 * `result.noAttestation` entries (neither thrown) per content-honesty
 * discipline canonical preservation.
 */
export async function verifyAttestationBatch(entries, trustedAttestationKeys) {
    const verified = [];
    const failed = [];
    const noAttestation = [];
    for (const entry of entries) {
        if (entry.attestation === undefined) {
            // Writer-side `disableLedger: true` per-entry — content-honesty
            // discipline at consumer surface: surface absent-attestation explicitly
            // rather than silent-filter. DEV5 §2(b) v0 shape `{ id }` only.
            noAttestation.push({ id: entry.id });
            continue;
        }
        const result = await verifyInclusionProofAttestation(entry.attestation, trustedAttestationKeys);
        if (result.valid) {
            verified.push({
                id: entry.id,
                attestation: entry.attestation,
                verifiedKid: result.verifiedKid,
                attestedAt: result.attestedAt,
            });
        }
        else {
            failed.push({
                id: entry.id,
                attestation: entry.attestation,
                reason: result.reason,
                detail: result.detail,
            });
        }
    }
    return { verified, failed, noAttestation };
}
//# sourceMappingURL=verify-attestation-batch.js.map